import {
  useRef,
  useState,
  useMemo,
  useCallback,
  useEffect,
  type KeyboardEvent,
} from 'react';
import { X } from 'lucide-react';
import { useAssetTags, type AssetTag } from '@Hooks/useAssetTags';
import type { AssetTagValue } from '@Types/assets';
import { cn } from '@Utils/cn';

export type { AssetTagValue };

interface AssetTagInputProps {
  value: AssetTagValue[];
  onChange: (tags: AssetTagValue[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export default function AssetTagInput({
  value,
  onChange,
  placeholder = 'Add tags...',
  disabled = false,
  className,
}: AssetTagInputProps) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const mirrorRef = useRef<HTMLSpanElement>(null);
  const [dropdownLeft, setDropdownLeft] = useState(0);

  const { data } = useAssetTags({}, { staleTime: 30_000 });
  const existingTags: AssetTag[] = data?.data ?? [];

  const selectedNames = useMemo(
    () => new Set(value.map((t) => t.name.toLowerCase())),
    [value],
  );

  const isAdded = useCallback(
    (name: string) => selectedNames.has(name.toLowerCase()),
    [selectedNames],
  );

  // Filter existing tags by query, excluding already-selected ones
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return existingTags.filter(
      (t) => t.name.toLowerCase().includes(q) && !isAdded(t.name),
    );
  }, [query, existingTags, isAdded]);

  // Whether the typed query exactly matches an existing tag
  const exactMatch = useMemo(
    () =>
      existingTags.find(
        (t) => t.name.toLowerCase() === query.trim().toLowerCase(),
      ),
    [existingTags, query],
  );

  // Build the suggestion list: existing matches + "create new" option
  const suggestions = useMemo(() => {
    const items: { key: string; label: string; tag: AssetTagValue }[] = [];

    for (const t of filtered) {
      items.push({ key: t.id, label: t.name, tag: { id: t.id, name: t.name } });
    }

    const trimmed = query.trim();
    if (trimmed && !exactMatch && !isAdded(trimmed)) {
      items.push({
        key: '__create__',
        label: `Create "${trimmed}"`,
        tag: { name: trimmed },
      });
    }

    return items;
  }, [filtered, query, exactMatch, isAdded]);

  const visible = showSuggestions && suggestions.length > 0;

  // Keep activeIndex in bounds
  useEffect(() => {
    setActiveIndex(0);
  }, [suggestions.length]);

  // Scroll active item into view
  useEffect(() => {
    if (!visible || !dropdownRef.current) return;
    const item = dropdownRef.current.children[activeIndex] as HTMLElement;
    item?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, visible]);

  // Measure the mirror span to position dropdown relative to where the user types
  useEffect(() => {
    if (!mirrorRef.current || !inputRef.current || !wrapperRef.current) return;
    const inputRect = inputRef.current.getBoundingClientRect();
    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    // Left edge of input relative to wrapper
    const inputLeft = inputRect.left - wrapperRect.left;
    setDropdownLeft(inputLeft);
  }, [query, value, visible]);

  // Close on outside click
  useEffect(() => {
    if (!visible) return;
    const handleClick = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [visible]);

  const addTag = useCallback(
    (tag: AssetTagValue) => {
      if (isAdded(tag.name)) return;
      onChange([...value, tag]);
      setQuery('');
      setShowSuggestions(false);
      inputRef.current?.focus();
    },
    [value, onChange, isAdded],
  );

  const removeTag = useCallback(
    (index: number) => {
      onChange(value.filter((_, i) => i !== index));
      inputRef.current?.focus();
    },
    [value, onChange],
  );

  const commitCurrent = useCallback(() => {
    const trimmed = query.trim();
    if (!trimmed) return;

    // If suggestions are visible and one is highlighted, use that
    if (visible && suggestions[activeIndex]) {
      addTag(suggestions[activeIndex].tag);
      return;
    }

    // Otherwise resolve against existing tags or create new
    if (exactMatch && !isAdded(exactMatch.name)) {
      addTag({ id: exactMatch.id, name: exactMatch.name });
    } else if (!isAdded(trimmed)) {
      addTag({ name: trimmed });
    }
  }, [query, visible, suggestions, activeIndex, exactMatch, isAdded, addTag]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitCurrent();
      return;
    }

    if (e.key === 'Backspace' && query === '' && value.length > 0) {
      removeTag(value.length - 1);
      return;
    }

    if (e.key === 'Escape') {
      setShowSuggestions(false);
      return;
    }

    if (!visible) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
    }
  };

  return (
    <div ref={wrapperRef} className={cn('relative', className)}>
      {/* The input container — pills + text input inline, wraps naturally */}
      <div
        className={cn(
          'flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border bg-white px-2 py-1.5 text-sm transition',
          'border-input focus-within:ring-ring/30 focus-within:border-ring focus-within:ring-2',
          disabled && 'cursor-not-allowed opacity-50',
        )}
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((tag, i) => (
          <span
            key={`${tag.name}-${i}`}
            className="bg-secondary text-secondary-foreground inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium"
          >
            {tag.name}
            <button
              type="button"
              aria-label={`Remove ${tag.name}`}
              onClick={(e) => {
                e.stopPropagation();
                removeTag(i);
              }}
              disabled={disabled}
              className="text-muted-foreground hover:text-foreground -mr-0.5 rounded-sm p-0.5 transition-colors"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}

        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setShowSuggestions(true);
            setActiveIndex(0);
          }}
          onFocus={() => {
            if (query.trim()) setShowSuggestions(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder={value.length === 0 ? placeholder : ''}
          disabled={disabled}
          className="placeholder:text-muted-foreground min-w-15 flex-1 bg-transparent text-sm outline-none"
          autoComplete="off"
        />

        {/* Hidden mirror span to measure input text width for dropdown positioning */}
        <span
          ref={mirrorRef}
          aria-hidden
          className="pointer-events-none invisible absolute text-sm whitespace-pre"
        >
          {query}
        </span>
      </div>

      {/* Suggestions dropdown */}
      {visible && (
        <ul
          ref={dropdownRef}
          role="listbox"
          className="border-input bg-popover text-popover-foreground absolute z-50 mt-1 max-h-48 overflow-y-auto rounded-md border py-1 shadow-md"
          style={{ left: dropdownLeft }}
        >
          {suggestions.map((item, i) => (
            <li
              key={item.key}
              role="option"
              aria-selected={i === activeIndex}
              className={cn(
                'cursor-pointer px-3 py-1.5 text-sm',
                i === activeIndex && 'bg-accent text-accent-foreground',
              )}
              onMouseEnter={() => setActiveIndex(i)}
              onMouseDown={(e) => {
                e.preventDefault(); // keep input focused
                addTag(item.tag);
              }}
            >
              {item.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
